import React from 'react';
import { useStableNavigate } from '@hooks/useStableRouter';
import { UserX } from 'lucide-react';
import { getProfilePath, isPathBuildable } from '@utils/slugHelpers';
import type { AskOnBehalfView } from '@utils/askOnBehalf';

interface AskedOnYourBehalfIndicatorProps {
  /**
   * The full ask-on-behalf view for this question (from `getAskOnBehalfView`).
   * Passing the whole object instead of loose `claimStatus` / `role` /
   * `originalAsker` / `beneficiary` props keeps the indicator in lockstep with
   * the helper and removes the `as ClaimStatus` casts at the call sites.
   */
  view: AskOnBehalfView;
  /**
   * Set by a host that gates navigation out of this card (QuestionCard's `blockNavigation`).
   * The OP name then calls this instead of routing to their profile, so the footer cannot be the
   * one link that walks a gated viewer out of the preview. Absent for the ungated hosts
   * (ProfileQuestionCard, QuestionDetails), which navigate as before.
   */
  onNavigationBlocked?: () => void;
}

export const AskedOnYourBehalfIndicator: React.FC<AskedOnYourBehalfIndicatorProps> = ({
  view,
  onNavigationBlocked,
}) => {
  // Rendered inside QuestionCard, so it inherits the card's constraint: useNavigate() would put the
  // router subscription this component's host just shed straight back into the feed, for the subset
  // of posts that carry an ask-on-behalf attribution. See stableRouterContext.ts.
  // AIDEV-NOTE: two of this component's three hosts - ProfileQuestionCard and QuestionDetails - still
  // call useNavigate()/useLocation() themselves, so the swap buys nothing there yet; it does make a
  // mounted StableRouterProvider a hard requirement for every host.
  const navigate = useStableNavigate();
  const { originalAsker, beneficiary } = view;
  const isDeclined = view.kind === 'declined';
  const isClaimed = view.kind === 'claimed';
  const isBeneficiary = view.role === 'beneficiary';
  const isOriginalAsker = view.role === 'original_asker';

  // Get display names with fallbacks - show first name only
  const opFirstName = originalAsker?.full_name?.split(' ')[0] || 'someone';
  const beneficiaryFirstName = beneficiary?.full_name?.split(' ')[0] || 'them';

  // AIDEV-NOTE: Defensive guard. Callers decide visibility via their own policy
  // (see getAskOnBehalfView consumers), but an observer should never see the
  // pending beneficiary-only copy — only claimed attribution or a declined note.
  // The claimed-attribution copy names the original asker, so for the observer
  // case only render it when there's actually someone to name. (Beneficiary and
  // original asker render their own claimed copy regardless via the role flags.)
  // This re-separates the data-presence check that the role refactor folded away.
  const hasOriginalAsker = Boolean(originalAsker);
  const shouldRender =
    isDeclined || isBeneficiary || isOriginalAsker || (isClaimed && hasOriginalAsker);

  if (!shouldRender) return null;

  // Handle OP name click - navigate to their profile
  const handleOpClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    // A gating host owns the outcome: this link has no auth check of its own, so without the
    // handover a guest in a gated preview walks straight out to the OP's profile.
    if (onNavigationBlocked) {
      onNavigationBlocked();
      return;
    }
    if (isPathBuildable(originalAsker)) {
      navigate(getProfilePath(originalAsker));
    }
  };

  // Render clickable OP name - uses span to avoid nested button issue
  // This is a render function (not a component) to avoid recreating during render
  const clickableOpName = isPathBuildable(originalAsker) ? (
    <span
      role="link"
      tabIndex={0}
      onClick={handleOpClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleOpClick(e);
        }
      }}
      className="text-mb-teal font-semibold hover:underline cursor-pointer"
    >
      {opFirstName}
    </span>
  ) : (
    <span className="text-mb-teal font-semibold">{opFirstName}</span>
  );

  // AIDEV-NOTE: Default copy is for the pending/unclaimed state.
  let content: React.ReactNode = 'This post was made for you';

  if (!isDeclined && !isClaimed) {
    if (isOriginalAsker) {
      content = (
        <span>
          You posted this to support{' '}
          <span className="text-mb-teal font-semibold">{beneficiaryFirstName}</span>
        </span>
      );
    } else if (isBeneficiary) {
      content = (
        <span>
          {clickableOpName} posted this to support{' '}
          <span className="text-mb-teal font-semibold">you</span>
        </span>
      );
    }
  }

  if (isClaimed) {
    if (isOriginalAsker) {
      // OP viewing their posted question that was claimed
      // "Originally posted by you to support {{beneficiary first name}}"
      content = (
        <span>
          Originally posted by <span className="text-mb-teal font-semibold">you</span> to support{' '}
          <span className="text-mb-teal font-semibold">{beneficiaryFirstName}</span>
        </span>
      );
    } else if (isBeneficiary) {
      // Beneficiary viewing their claimed question
      // "Originally posted by {{OP}} to support you"
      content = (
        <span>
          Originally posted by {clickableOpName} to support{' '}
          <span className="text-mb-teal font-semibold">you</span>
        </span>
      );
    } else {
      // Third party viewing a claimed question
      // "Originally posted by {{OP}} to support {{beneficiary first name}}"
      content = (
        <span>
          Originally posted by {clickableOpName} to support{' '}
          <span className="text-mb-teal font-semibold">{beneficiaryFirstName}</span>
        </span>
      );
    }
  } else if (isDeclined) {
    if (isBeneficiary) {
      content = 'You declined this post';
    } else {
      content = 'This post was declined';
    }
  }

  const renderIcon = () => {
    if (isDeclined) {
      return (
        <div className="flex h-6 w-6 items-center justify-center rounded-full p-1 bg-destructive/90">
          <UserX className="w-4 h-4 text-white" aria-hidden="true" />
        </div>
      );
    }

    return (
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-mb-teal/20 p-1">
        <img src="/ask-on-your-behalf.svg" alt="" className="h-6 w-6" />
      </div>
    );
  };

  return (
    <button
      type="button"
      className="relative w-full text-left"
      onClick={e => {
        e.stopPropagation();
      }}
      aria-label="Post made for someone"
    >
      <div className="absolute inset-x-0 top-0 flex justify-center z-10">
        <div className="h-px w-full bg-mb-beige" />
        <div className="absolute -top-[4px] h-[10px] w-[10px] rotate-45 border-b border-r border-mb-beige bg-neutral-50" />
      </div>
      <div className="flex w-full items-center justify-center gap-1 pt-3 pb-2">
        {renderIcon()}
        <span className="font-fira text-xs text-black">{content}</span>
      </div>
    </button>
  );
};
